import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, Building2, Loader2, CheckCircle } from 'lucide-react';
import { api } from '../../api/client';
import PortalLayout from './PortalLayout';

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors';

export default function PortalProfile() {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [toast, setToast] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['portal-profile'],
    queryFn: () => api.portal.profile().then(r => r.data.data),
  });

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setEmail(profile.email || '');
      setPhone(profile.phone_number || '');
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.portal.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-profile'] });
      setPassword('');
      setConfirmPassword('');
      setToast('Profile updated successfully');
      setTimeout(() => setToast(''), 3000);
    },
    onError: (err: any) => {
      setToast(err.response?.data?.error || 'Update failed');
      setTimeout(() => setToast(''), 3000);
    },
  });

  const handleSave = () => {
    const data: any = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneNumber: phone.trim(),
    };
    if (password) {
      if (password.length < 8) {
        setToast('Password must be at least 8 characters');
        setTimeout(() => setToast(''), 3000);
        return;
      }
      if (password !== confirmPassword) {
        setToast('Passwords do not match');
        setTimeout(() => setToast(''), 3000);
        return;
      }
      data.password = password;
    }
    updateMutation.mutate(data);
  };

  return (
    <PortalLayout>
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-fieldiq-accent" />
        </div>
      ) : (
        <div className="space-y-4 animate-fade-in max-w-lg">
          <h2 className="text-white font-bold text-lg">My Profile</h2>

          {/* Profile Form */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <User size={16} className="text-fieldiq-accent" />
              <h3 className="text-xs font-bold text-white tracking-wider uppercase">Account Details</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-fieldiq-muted block mb-1">First Name</label>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted block mb-1">Last Name</label>
                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} />
              </div>
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted block mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted block mb-1">Phone</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 space-y-4">
            <h3 className="text-xs font-bold text-white tracking-wider uppercase">Change Password</h3>

            <div>
              <label className="text-xs text-fieldiq-muted block mb-1">New Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className={inputClass} placeholder="Min 8 characters" />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted block mb-1">Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Company Info (read-only) */}
          {profile?.customer_name && (
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={16} className="text-fieldiq-accent" />
                <h3 className="text-xs font-bold text-white tracking-wider uppercase">Company Info</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-fieldiq-muted">Company</span>
                  <span className="text-white">{profile.customer_name}</span>
                </div>
                {profile.customer_city && (
                  <div className="flex justify-between">
                    <span className="text-fieldiq-muted">Location</span>
                    <span className="text-white">{[profile.customer_city, profile.customer_province_state].filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="w-full py-3 bg-fieldiq-accent text-black font-bold rounded-xl text-sm hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {updateMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-xl shadow-lg text-sm animate-fade-in z-50 flex items-center gap-2">
          {toast.includes('success') && <CheckCircle size={14} className="text-green-400" />}
          {toast}
        </div>
      )}
    </PortalLayout>
  );
}
